import { Tagged } from 'type-fest';
import { Updater } from 'use-immer';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import type {
  AdminSection,
  Payload,
  PlatformApp,
  PlatformAppInstance,
} from '@typings';
import { SUPPORTED_GPU_MAKES } from '@constants';

import { Billing as BillingType } from './billing';
import { Job } from './job';

export type AnyObject = Record<string, any>;

export type AnyFunction = (...args: any[]) => any;

export type AsyncFunction = (...args: any[]) => Promise<any>;

export type Primitive = number | string | null | undefined;

export type NumericString = `${number}`;

export type ImageUrl = Tagged<'ImageURL', string>;

export enum Role {
  Reader = 'reader',
  Writer = 'writer',
  Manager = 'manager',
  Admin = 'admin',
}

export enum ProjectRole {
  Reader = 'reader',
  Writer = 'writer',
  Manager = 'manager',
}

export enum TenantRole {
  User = 'user',
  Manager = 'manager',
  Admin = 'admin',
}

export type Project = {
  name: string;
  orgName: string | null;
  role: Role;
  clusterName: string;
};

export type NoOrganization = null;

export namespace Organization {
  export type Name = string | NoOrganization;

  export type Model = {
    name: string;
    role: TenantRole;
  };
}

export type SidebarItem = {
  regularIcon: IconProp;
  solidIcon: IconProp;
  name: string;
  path: string | string[];
};

export type SupportedGpuMake = (typeof SUPPORTED_GPU_MAKES)[number];

export namespace Gpu {
  /**
   * New GPU model configuration
   */
  export type Spec = {
    count: number;
    model?: string;
    memory?: number;
  };

  export type RecordNameOld = {
    [K in `${SupportedGpuMake}GpuModel`]?: string;
  };

  export type Record = {
    [K in `${SupportedGpuMake}Gpu`]?: Spec;
  };

  export type RecordOld = RecordNameOld & {
    [K in `${SupportedGpuMake}Gpu`]?: number;
  };

  export type ProfileModel = {
    gpuMake: SupportedGpuMake;
    gpuModelName?: Spec['model'];
    gpuMemory?: Spec['memory'];
  };

  export type Profile = ProfileModel & {
    gpuQuantity?: Spec['count'];
  };

  export type Model = Record | RecordOld;
}

/**
 * todo: think about to move it to cluster.ts
 */
export namespace Cluster {
  export type ResourcePoolType = {
    availableCpu: number;
    availableDiskSize: number;
    availableMemory: number;
    cpu: number;
    cpuMaxWatts?: number;
    cpuMinWatts?: number;
    diskSize: number;
    idleSize?: number;
    isPreemptible?: boolean;
    maxSize: number;
    memory: number;
    minSize: number;
    name: string;
  } & Gpu.Model;

  export type ResourcePreset = {
    cpu: number;
    creditsPerHour: string | number;
    isPreemptible: boolean;
    isPreemptibleNodeRequired: boolean;
    memory: number;
    memoryMb: number;
    name: string;
    preemptibleNode: boolean;
    schedulerEnabled: boolean;
    resourcePoolNames?: string[];
    availableResourcePoolNames: string[];
  } & Gpu.Model;

  export type StorageVolume = {
    name: string;
    creditsPerHourPerGb: string;
  };

  export type Model = {
    name: string;
    orgs: Array<string | null>;
    projects: Project[];
    metricsUrl: string;
    monitoringUrl: string;
    secretsUrl: string;
    storageUrl: string;
    disksUrl: string;
    bucketsUrl: string;
    registryUrl: string;
    usersUrl: string;
    resourcePoolTypes: ResourcePoolType[];
    resourcePresets: ResourcePreset[];
    storageVolumes: StorageVolume[];
    location?: string;
    logoUrl?: string;
  };
}

export type ModalProps = {
  closeModal?: () => void;
};

export type FieldProps = {
  required?: boolean;
  className?: string;
  containerClassName?: string;
  variant?: 'medium' | 'smaller';
  labelVariant?: 'floating' | 'static';
  label?: string;
  showError?: boolean;
  error?: string;
  note?: string;
  children?: React.ReactNode;
};

export type ResponseError = {
  error: string;
};

/**
 * Used for dayjs
 */
export type DateFormat = Tagged<string, 'DateFormat'>;

/**
 * Milliseconds
 */
export type Timestamp = Tagged<number, 'Timestamp'>;

/**
 * Seconds
 */
export type UnixTimestamp = Tagged<number, 'UnixTimestamp'>;

export type UnixTimestampUtc = Tagged<number, 'UnixTimestampUTC'>;

export type Secret = {
  key: string;
  orgName: Organization.Name;
  owner: string;
  projectName: string;
};

export type Disk = {
  id: string;
  name?: string | null;
  createdAt: DateFormat;
  lastUsage: null;
  lifeSpan?: number;
  owner: string;
  status: string;
  storage: number;
  usedBytes: null;
  uri?: string;
};

export enum Permission {
  Read = 'read',
  Write = 'write',
  Manage = 'manage',
}

export type PermissionModel = {
  uri: string;
  action: Permission;
};

export type PermissionUri = 'blob:' | 'cluster:';

export enum Mode {
  ReadWrite = 'rw',
  ReadOnly = 'ro',
}

export namespace Image {
  export type Name = string;

  export type Manifest = {
    imageSizeBytes: string;
    mediaType: string;
    tag: string[];
    timeCreatedMs: string;
    timeUploadedMs: string;
  };

  export type Model = {
    child: string[];
    manifest: { [key: string]: Manifest };
    name: Name;
    tags: string[];
  };
}

export type JobNavigatorItem = {
  title: string;
  name: string;
};

export enum VolumeType {
  Secret = 'secret',
  Storage = 'storage',
  Disk = 'disk',
}

export namespace Flow {
  export type BakeLastAttempt = {
    id: string;
    bakeId: string;
    number: number;
    createdAt: DateFormat;
    result: Job.Status;
    executorId: string;
    configsMeta: AnyObject;
  };

  export type BakeGraphs = {
    [key: string]: {
      [key: string]: string[];
    };
  };

  export type Bake = {
    id: string;
    projectId: string;
    batch: string;
    createdAt: DateFormat;
    tags: string[];
    name: string | null;
    graphs: BakeGraphs;
    lastAttempt: BakeLastAttempt | null;
  };

  export type BakeTask = {
    yamlId: string;
    outputs: null & { [key: string]: string };
    statuses: Job.History[];
    rawId: string | null;
  };

  export type Model = {
    cluster: string;
    id: string;
    name: string;
    orgName: Organization.Name;
    owner: string;
    projectName: string;
  };
}

export type Balance = {
  spentCredits: string;
  credits?: NumericString;
};

export enum ViewType {
  Grid,
  List,
}

export enum Hostname {
  Scottdc = 'scottdc',
  Novoserve = 'novoserve',
  Geico = 'geico',
  Onue = 'onue',
  Imhpc = 'imhpc',
  Megaport = 'megaport',
  Wwt = 'wwt-pg',
  Herbalife = 'herbalife',
}

export namespace OutletContext {
  export type Job = {
    setTabTitle: (tab: Job.Tab) => void;
  };

  export type Billing = {
    params: BillingType.Params;
    billing: BillingType.State;
    setTabTitle: (tab: BillingType.Tab) => void;
  };

  export type AppConstructor = {
    submitting: boolean;
    app: Job.Item;
    handleAppSubmit: (payload: Payload.AppConstructor) => Promise<void>;
  };

  export type PlatformAppsInstance = {
    instance: PlatformApp.Instance;
    input: AnyObject | string | null;
    output: AnyObject | string | null;
    events: PlatformAppInstance.Event[] | null;
    getInstance: () => Promise<PlatformApp.Instance>;
    setTabTitle: (tab: Job.Tab) => void;
  };

  export type AdminSectionCluster = {
    cluster: AdminSection.Cluster & {
      orgs: AdminSection.ClusterOrganization[];
    };
    allClusterOrganizations: AdminSection.ClusterOrganization[];
    setCluster: Updater<AdminSectionCluster['cluster']>;
    setRightTabComponent: (component: React.ReactNode | null) => void;
  };
}

export type TelemetryData = {
  /**
   * Actially can be `undefined`
   */
  gpuMemory: number;
  /**
   * Actially can be `undefined`
   */
  gpuDutyCycle: number;
  cpu: number;
  memory: number;
  timestamp: UnixTimestamp;
};

/**
 * Is used for observability tab to manage empty chart columns
 *
 * todo: try to optimize, DRY
 */
export type TelemetryItem = {
  /**
   * Actially can be `undefined`
   */
  gpuMemory: number | null;
  /**
   * Actially can be `undefined`
   */
  gpuDutyCycle: number | null;
  cpu: number | null;
  memory: number | null;
  timestamp: UnixTimestamp;
};

export namespace OrganizationInvite {
  export enum Status {
    Pending = 'pending',
    Expired = 'expired',
    Accepted = 'accepted',
    Declined = 'declined',
  }

  export type Interface = {
    expiredAt: DateFormat;
    email: string;
    id: string;
    organizationName: string;
    role: TenantRole;
    status: Status;
  };

  export type Response = Pick<
    Interface,
    'expiredAt' | 'email' | 'id' | 'status'
  > & {
    orgName: string;
    roleName: TenantRole;
  };
}

export namespace OrganizationSettings {
  export type Model = {
    userDefaultCredits?: string;
    balance: Balance;
  };
}

export type UserOrganization = {
  orgName: Extract<Organization.Name, string>;
  balance: Balance;
};

export type WithPagination<Item> = {
  page: number;
  pages: number;
  total: number;
  size: number;
  items: Item[];
};

/**
 * [`app type name`, `property field name`]
 */
export type CustomComponentName = readonly [string, string];

export type SchemaNodeProps = {
  withFormatter?: boolean;
};

export type DashboardLink = {
  /**
   * Examples:
   * - p(metricsUrl, 'd', source, source) > https://grafana.dev.apolo.us/d/overview/overview
   */
  source: string;
  /**
   * Examples:
   * - p(metricsUrl, 'd', resource, source) > https://grafana.dev.apolo.us/d/resources/cluster-resources
   *
   * If not provided, `source` will be used instead
   */
  resource?: string;
  title?: string;
  refreshTimeoutMinutes?: string;
  params?: Record<string, any>;
};
